/**
 * Client-side US ZIP → state approximation. Dealers have no `state` column in the MCP (the
 * dealers table stores only `primary_zip`), so we derive the state from the ZIP for display.
 *
 * This is an approximation: it maps the 5-digit ZIP to the state that owns that numeric range.
 * Full 5-digit ranges are accurate for the vast majority of addresses; a handful of boundary /
 * special ZIPs (military, some DC/MD/VA edges) may be off, and non-US or malformed ZIPs return
 * null (rendered as "—"). If dealers ever need an authoritative state, add a `state` column to
 * the dealers table + `listDealers` in cabana-mcp and read it directly instead.
 */

interface ZipRange {
  min: number;
  max: number;
  state: string;
}

// Ordered, non-overlapping numeric ranges over the 5-digit ZIP. Covers 50 states + DC + PR/VI.
const RANGES: ZipRange[] = [
  { min: 600, max: 799, state: 'PR' },
  { min: 800, max: 899, state: 'VI' },
  { min: 900, max: 999, state: 'PR' },
  { min: 1000, max: 2799, state: 'MA' },
  { min: 2800, max: 2999, state: 'RI' },
  { min: 3000, max: 3899, state: 'NH' },
  { min: 3900, max: 4999, state: 'ME' },
  { min: 5000, max: 5999, state: 'VT' },
  { min: 6000, max: 6999, state: 'CT' },
  { min: 7000, max: 8999, state: 'NJ' },
  { min: 10000, max: 14999, state: 'NY' },
  { min: 15000, max: 19699, state: 'PA' },
  { min: 19700, max: 19999, state: 'DE' },
  { min: 20000, max: 20599, state: 'DC' },
  { min: 20600, max: 21999, state: 'MD' },
  { min: 22000, max: 24699, state: 'VA' },
  { min: 24700, max: 26899, state: 'WV' },
  { min: 27000, max: 28999, state: 'NC' },
  { min: 29000, max: 29999, state: 'SC' },
  { min: 30000, max: 31999, state: 'GA' },
  { min: 32000, max: 34999, state: 'FL' },
  { min: 35000, max: 36999, state: 'AL' },
  { min: 37000, max: 38599, state: 'TN' },
  { min: 38600, max: 39799, state: 'MS' },
  { min: 40000, max: 42799, state: 'KY' },
  { min: 43000, max: 45999, state: 'OH' },
  { min: 46000, max: 47999, state: 'IN' },
  { min: 48000, max: 49999, state: 'MI' },
  { min: 50000, max: 52999, state: 'IA' },
  { min: 53000, max: 54999, state: 'WI' },
  { min: 55000, max: 56799, state: 'MN' },
  { min: 57000, max: 57799, state: 'SD' },
  { min: 58000, max: 58899, state: 'ND' },
  { min: 59000, max: 59999, state: 'MT' },
  { min: 60000, max: 62999, state: 'IL' },
  { min: 63000, max: 65999, state: 'MO' },
  { min: 66000, max: 67999, state: 'KS' },
  { min: 68000, max: 69399, state: 'NE' },
  { min: 70000, max: 71599, state: 'LA' },
  { min: 71600, max: 72999, state: 'AR' },
  { min: 73000, max: 74999, state: 'OK' },
  { min: 75000, max: 79999, state: 'TX' },
  { min: 80000, max: 81999, state: 'CO' },
  { min: 82000, max: 83199, state: 'WY' },
  { min: 83200, max: 83999, state: 'ID' },
  { min: 84000, max: 84999, state: 'UT' },
  { min: 85000, max: 86999, state: 'AZ' },
  { min: 87000, max: 88499, state: 'NM' },
  { min: 88900, max: 89999, state: 'NV' },
  { min: 90000, max: 96199, state: 'CA' },
  { min: 96700, max: 96899, state: 'HI' },
  { min: 97000, max: 97999, state: 'OR' },
  { min: 98000, max: 99499, state: 'WA' },
  { min: 99500, max: 99999, state: 'AK' },
];

/** Two-letter US state (or PR/VI) for a ZIP, or null when absent/non-US/unmappable. */
export function zipToState(zip: string | null | undefined): string | null {
  if (zip == null) return null;
  const digits = zip.trim().slice(0, 5); // tolerate ZIP+4 ("78701-1234")
  if (!/^\d{3,5}$/.test(digits)) return null;
  const n = Number(digits);
  for (const r of RANGES) {
    if (n >= r.min && n <= r.max) return r.state;
  }
  return null;
}
